import type { DateInvite, DateInviteStatus } from '@/api/types';

// ---- the link
// The server stores only a hash of the link token, so it hands the URL to the host once (on create /
// new link). We keep it in this browser so "Copy link" works later; another device mints a new link.
const LINKS_KEY = 'popin.inviteLinks';

function readLinks(): Record<string, string> {
  try {
    const raw = localStorage.getItem(LINKS_KEY);
    const v = raw ? (JSON.parse(raw) as unknown) : null;
    return v && typeof v === 'object' ? (v as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function rememberLink(id: string, url: string | undefined): void {
  if (!url) return;
  try {
    const all = readLinks();
    all[id] = url;
    // Keep the newest 30 (hosts have ≤ 5 live invites).
    const entries = Object.entries(all).slice(-30);
    localStorage.setItem(LINKS_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    /* storage blocked: the share sheet still has the link this time */
  }
}

export function linkFor(id: string): string | null {
  const v = readLinks()[id];
  return typeof v === 'string' && /^https?:\/\//.test(v) ? v : null;
}

// ---- copy

/** Copy text: async Clipboard API, falling back to a hidden textarea + execCommand. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* permission or insecure context: try the fallback */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// ---- share

export function shareMessage(inv: Pick<DateInvite, 'length_min' | 'starts_at' | 'slots'>, url: string): string {
  const n = inv.slots?.length ?? 0;
  const when = n > 0 ? ` I picked ${n === 1 ? 'a time' : `${n} times`} — tell me what works.` : inv.starts_at ? ` ${whenShort(inv.starts_at)}` : '';
  return `Want to go on a first date with me on Popin?${when} It’s a ${inv.length_min}-minute guided video date — fun questions, a game or two, skip anything. ${url}`;
}

export function whatsappHref(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export const canNativeShare = (): boolean => typeof navigator !== 'undefined' && typeof navigator.share === 'function';

/** Web Share API; false when unavailable or the person backed out. */
export async function nativeShare(title: string, text: string, url: string): Promise<boolean> {
  if (!canNativeShare()) return false;
  try {
    await navigator.share({ title, text, url });
    return true;
  } catch {
    return false;
  }
}

// ---- time wording

const dayMs = 86_400_000;

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "tonight at 8:30 PM", "tomorrow at 7 PM", "Fri 17 Oct at 8 PM" (viewer's local time). */
export function whenShort(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: d.getMinutes() ? '2-digit' : undefined });
  if (sameDay(d, now)) return `${d.getHours() >= 17 ? 'tonight' : 'today'} at ${time}`;
  if (sameDay(d, new Date(now.getTime() + dayMs))) return `tomorrow at ${time}`;
  return `${d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} at ${time}`;
}

/** "Now — whenever you're both ready" or "Tonight at 8:30 PM". */
export function whenLabel(startsAt: string | null, now = new Date()): string {
  if (!startsAt) return 'Whenever you’re both ready';
  const s = whenShort(startsAt, now);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const STATUS_LABEL: Record<DateInviteStatus, string> = {
  open: 'Waiting for a yes',
  planning: 'Planning',
  awaiting_host: 'Pick a time',
  scheduled: 'It’s a date',
  accepted: 'Accepted',
  declined: 'Declined',
  cancelled: 'Cancelled',
  expired: 'Expired',
  completed: 'Done',
};

/** The other person on an invite (null for an open invite seen by its host). */
export function otherPerson(inv: DateInvite) {
  return inv.role === 'host' ? inv.guest : inv.host;
}

const LIVE: DateInviteStatus[] = ['open', 'planning', 'awaiting_host', 'scheduled', 'accepted'];
export const isLive = (inv: Pick<DateInvite, 'status'>) => LIVE.includes(inv.status);

/** A date that's on: a "now" invite with a guest, or a confirmed time (v6.2). */
export const isLobbyable = (inv: Pick<DateInvite, 'status'>) => inv.status === 'accepted' || inv.status === 'scheduled';

/** Someone said yes (v6.2 adds the planning statuses). The lobby and Start work from here on: the time is a plan, not a lock. */
export const hasGuest = (inv: Pick<DateInvite, 'status'>) => inv.status !== 'open' && isLive(inv);

// ---- calendar (.ics)

function icsDate(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function icsEscape(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

/** A minimal RFC 5545 event for a scheduled date (with the lobby link). */
export function buildIcs(inv: Pick<DateInvite, 'id' | 'starts_at' | 'length_min'>, withName: string | null, url: string, now = new Date()): string {
  const start = inv.starts_at ? new Date(inv.starts_at) : now;
  const end = new Date(start.getTime() + inv.length_min * 60_000);
  const title = withName ? `Popin date with ${withName}` : 'Popin date';
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Popin//Dates//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${inv.id}@popin`,
    `DTSTAMP:${icsDate(now)}`,
    `DTSTART:${icsDate(start)}`,
    `DTEND:${icsDate(end)}`,
    `SUMMARY:${icsEscape(title)}`,
    `DESCRIPTION:${icsEscape(`A ${inv.length_min}-minute guided video date. Join anytime: start as soon as you’re both there. ${url}`)}`,
    `URL:${icsEscape(url)}`,
    'BEGIN:VALARM',
    'TRIGGER:-PT15M',
    'ACTION:DISPLAY',
    `DESCRIPTION:${icsEscape(title)}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].join('\r\n');
}

export function downloadIcs(ics: string, filename = 'popin-date.ics'): void {
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The lobby link for either person (guests never have the invite token). */
export function lobbyUrl(id: string): string {
  return `${window.location.origin}/dates/${id}`;
}
